/**
 * Customer Tags Management Component
 * Extracted from Settings.js for better maintainability
 */
import { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { toast } from 'sonner';
import { Plus, Loader2, Trash2, Edit, Tag, Percent } from 'lucide-react';

const TAG_COLORS = ['gray', 'blue', 'green', 'yellow', 'red', 'purple', 'orange'];

const getTagColorClass = (color) => {
  const classes = {
    gray: 'bg-gray-500',
    blue: 'bg-blue-500',
    green: 'bg-green-500',
    yellow: 'bg-yellow-500',
    red: 'bg-red-500',
    purple: 'bg-purple-500',
    orange: 'bg-orange-500',
  };
  return classes[color] || 'bg-gray-500';
};

const getTagBadgeClass = (color) => {
  const classes = {
    gray: 'bg-gray-500/10 text-gray-700 border-gray-500/30',
    blue: 'bg-blue-500/10 text-blue-700 border-blue-500/30',
    green: 'bg-green-500/10 text-green-700 border-green-500/30',
    yellow: 'bg-yellow-500/10 text-yellow-700 border-yellow-500/30',
    red: 'bg-red-500/10 text-red-700 border-red-500/30',
    purple: 'bg-purple-500/10 text-purple-700 border-purple-500/30',
    orange: 'bg-orange-500/10 text-orange-700 border-orange-500/30',
  };
  return classes[color] || 'bg-gray-500/10 text-gray-700 border-gray-500/30';
};

export function CustomerTagsManager({ api, outletId }) {
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTag, setEditingTag] = useState(null);
  const [form, setForm] = useState({
    name: '',
    color: 'gray',
    description: '',
    discount_percent: '',
  });

  useEffect(() => {
    if (outletId) {
      fetchTags();
    }
  }, [outletId]);

  const fetchTags = async () => {
    try {
      const resp = await api.get('/customer-tags/all', {
        params: { outlet_id: outletId },
      });
      setTags(resp.data);
    } catch (error) {
      console.error('Failed to fetch tags:', error);
    }
  };

  const openDialog = (tag = null) => {
    setEditingTag(tag);
    if (tag) {
      setForm({
        name: tag.name,
        color: tag.color || 'gray',
        description: tag.description || '',
        discount_percent: tag.discount_percent || '',
      });
    } else {
      setForm({
        name: '',
        color: 'gray',
        description: '',
        discount_percent: '',
      });
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error('Nama tag wajib diisi');
      return;
    }
    
    setLoading(true);
    try {
      const data = {
        name: form.name.trim(),
        color: form.color,
        description: form.description || '',
        discount_percent: form.discount_percent ? parseFloat(form.discount_percent) : 0,
      };
      
      if (editingTag) {
        await api.put(`/customer-tags/${editingTag.id}`, data);
        toast.success('Tag berhasil diperbarui');
      } else {
        await api.post('/customer-tags', data, {
          params: { outlet_id: outletId },
        });
        toast.success('Tag berhasil dibuat');
      }
      
      setDialogOpen(false);
      setEditingTag(null);
      fetchTags();
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error('Tag dengan nama tersebut sudah ada');
      } else {
        toast.error('Gagal menyimpan tag');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (tag) => {
    if (!window.confirm(`Hapus tag "${tag.name}"? Tag akan dihapus dari semua pelanggan.`)) return;
    try {
      await api.delete(`/customer-tags/${tag.id}`);
      toast.success('Tag berhasil dihapus');
      fetchTags();
    } catch (error) {
      toast.error('Gagal menghapus tag');
    }
  };

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Tag className="h-5 w-5 text-purple-600" />
                Tag Pelanggan
              </CardTitle>
              <CardDescription>
                Kelola tag untuk mengkategorikan pelanggan (VIP, Regular, dll)
              </CardDescription>
            </div>
            <Button onClick={() => openDialog()} data-testid="add-tag-btn">
              <Plus className="h-4 w-4 mr-2" />
              Tambah Tag
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {tags.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">
              Belum ada tag pelanggan. Tambahkan tag untuk mengkategorikan pelanggan Anda.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Tag</TableHead>
                  <TableHead>Deskripsi</TableHead>
                  <TableHead>Diskon Otomatis</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Aksi</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tags.map((tag) => (
                  <TableRow key={tag.id} data-testid={`tag-row-${tag.id}`}>
                    <TableCell>
                      <Badge className={getTagBadgeClass(tag.color)}>
                        <div className={`w-2 h-2 rounded-full ${getTagColorClass(tag.color)} mr-1.5`} />
                        {tag.name}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {tag.description || '-'}
                    </TableCell>
                    <TableCell>
                      {tag.discount_percent > 0 ? (
                        <Badge variant="outline" className="text-green-600">
                          <Percent className="h-3 w-3 mr-1" />
                          {tag.discount_percent}%
                        </Badge>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant={tag.is_active ? 'default' : 'secondary'}>
                        {tag.is_active ? 'Aktif' : 'Nonaktif'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openDialog(tag)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(tag)}
                          className="text-destructive hover:text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Tag Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md" data-testid="tag-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Tag className="h-5 w-5 text-purple-600" />
              {editingTag ? 'Edit Tag' : 'Tambah Tag Baru'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Nama Tag *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Contoh: VIP, Regular, Member"
                data-testid="tag-name-input"
              />
            </div>
            
            <div>
              <Label>Warna</Label>
              <Select 
                value={form.color} 
                onValueChange={(v) => setForm({ ...form, color: v })}
              >
                <SelectTrigger data-testid="tag-color-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TAG_COLORS.map((color) => (
                    <SelectItem key={color} value={color}>
                      <div className="flex items-center gap-2">
                        <div className={`w-4 h-4 rounded ${getTagColorClass(color)}`} />
                        {color.charAt(0).toUpperCase() + color.slice(1)}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Deskripsi</Label>
              <Input
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Deskripsi singkat (opsional)"
                data-testid="tag-description-input"
              />
            </div>
            
            <div>
              <Label>Diskon Otomatis (%)</Label>
              <Input
                type="number"
                min="0"
                max="100"
                value={form.discount_percent}
                onChange={(e) => setForm({ ...form, discount_percent: e.target.value })}
                placeholder="0"
                data-testid="tag-discount-input"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Diskon yang otomatis diterapkan untuk pelanggan dengan tag ini
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Batal
            </Button>
            <Button onClick={handleSave} disabled={loading} className="bg-purple-600 hover:bg-purple-700">
              {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {editingTag ? 'Simpan Perubahan' : 'Tambah Tag'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default CustomerTagsManager;
